import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
    Briefcase, Users, Target, Building, AlertTriangle, CheckCircle, RefreshCw, Star, Trophy, Award, 
    TrendingUp, GraduationCap, Sparkles, ArrowRight, Phone, Mail, Clock, MapPin, Heart, Shield, 
    Globe, Code, Server, Layers, CloudCog, BarChart, Bot, ChevronDown, Check, PenTool, User
} from 'lucide-react';
import hiringPartnersGrid from '../assets/hiring-partners-grid.jpg';
import api from '../utils/axios';

// Enhanced components
const StatCard = ({ icon: Icon, number, label, description, color, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-8 h-8 text-white" />
        </div>
        <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {number}
        </div>
        <div className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">{label}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>
    </motion.div>
);

const RoleCard = ({ icon, title, description, skills, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 h-full group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center justify-center h-16 w-16 mb-6 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mx-auto group-hover:scale-110 transition-transform duration-300">
            {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6 leading-relaxed">
            {description}
        </p>
        <div className="space-y-2">
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Key Skills:</h4>
            <div className="flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                    <span key={index} className="bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200 text-xs font-medium px-3 py-1 rounded-full">
                        {skill}
                    </span>
                ))}
            </div>
        </div>
    </motion.div>
);

const ProcessStep = ({ number, title, description, icon: Icon, isLast, delay = 0 }) => (
    <motion.div 
        className="flex items-start"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex flex-col items-center mr-6">
            <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-xl z-10 shadow-lg">
                <Icon className="w-8 h-8" />
            </div>
            {!isLast && <div className="w-px h-24 bg-gradient-to-b from-primary-300 to-transparent dark:from-primary-700 mt-4"></div>}
        </div>
        <div className="flex-1 pb-12">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700">
                <div className="flex items-center mb-3">
                    <span className="bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 text-sm font-bold px-3 py-1 rounded-full mr-3">
                        Step {number}
                    </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{description}</p>
            </div>
        </div>
    </motion.div>
);

const TestimonialCard = ({ name, role, company, testimonial, rating, delay = 0 }) => (
    <motion.div 
        className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 dark:border-gray-700 group hover:-translate-y-2"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
    >
        <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-4 group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
            </div>
            <div>
                <h4 className="font-bold text-gray-900 dark:text-white text-lg">{name}</h4>
                <p className="text-primary-600 dark:text-primary-400 font-medium">{role}</p>
                <p className="text-sm text-gray-500">{company}</p>
            </div>
        </div>
        <div className="flex items-center mb-4">
            {[...Array(rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
            ))}
        </div>
        <blockquote className="text-gray-700 dark:text-gray-300 italic leading-relaxed">
            "{testimonial}"
        </blockquote>
    </motion.div>
);

// Main Component
const Hire = () => {
    const { register, handleSubmit, formState: { errors }, reset, watch } = useForm();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');
    const [progress, setProgress] = useState(0);

    const formData = watch();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Calculate form progress
    useEffect(() => {
        if (!formData) {
            setProgress(0);
            return;
        }
        
        const requiredFields = ['companyName', 'contactPerson', 'email', 'phone', 'roleType', 'experience', 'positions', 'urgency'];
        const filledFields = requiredFields.filter(field => {
            const value = formData[field];
            return value && value.toString().trim() !== '';
        }).length;
        const totalFields = requiredFields.length;
        const calculatedProgress = Math.round((filledFields / totalFields) * 100);
        setProgress(calculatedProgress);
    }, [formData]);

    const onSubmit = async (data) => {
        setStatus('loading');
        setErrorMessage('');
        
        try {
            const response = await api.post('/hiring-requests', {
                companyName: data.companyName,
                contactPerson: data.contactPerson,
                email: data.email,
                phone: data.phone,
                roleType: data.roleType,
                experience: data.experience,
                positions: parseInt(data.positions),
                urgency: data.urgency,
                requirements: data.requirements || ''
            });

            if (response.data.success) {
                setStatus('success');
                setIsSubmitted(true);
                reset();
                // Reset form after 5 seconds
                setTimeout(() => {
                    setStatus('idle');
                    setIsSubmitted(false);
                }, 5000);
            } else {
                setErrorMessage(response.data.message || 'Failed to submit hiring request. Please try again.');
                setStatus('error');
            }
        } catch (err) {
            // Handle different types of errors
            if (err.response) {
                // Server responded with error status
                const errorData = err.response.data;
                if (errorData.message) {
                    setErrorMessage(errorData.message);
                } else if (err.response.status === 400) {
                    setErrorMessage('Please check your form data and try again.');
                } else if (err.response.status === 500) {
                    setErrorMessage('Server error. Please try again later.');
                } else {
                    setErrorMessage('Something went wrong. Please try again.');
                }
            } else if (err.request) {
                // Network error - no response received
                setErrorMessage('Network error. Please check your internet connection and try again.');
            } else {
                // Other errors
                setErrorMessage('An unexpected error occurred. Please try again.');
            }
            
            setStatus('error');
        }
    };
    
    // Animation variants
    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerContainer = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 100
            }
        },
    };
    
    const Input = React.useCallback(({ name, type = 'text', placeholder, validation, error, register }) => (
        <div>
            <label htmlFor={name} className="font-medium text-gray-700 dark:text-gray-300 mb-2 block text-sm">
                {placeholder}
            </label>
            <input 
                id={name}
                type={type}
                {...register(name, validation)}
                className={`w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border-2 transition-all duration-300
                    ${error ? 'border-red-500 focus:border-red-500 ring-red-500/20' : 'border-gray-200 dark:border-gray-600 focus:border-primary-500 ring-primary-500/20'}
                    text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2`}
            />
            {error && <p className="text-red-500 text-sm mt-1.5">{error.message}</p>}
        </div>
    ), []);
    
    const Select = React.useCallback(({ name, placeholder, validation, error, register, children }) => (
         <div>
            <label htmlFor={name} className="font-medium text-gray-700 dark:text-gray-300 mb-2 block text-sm">
                {placeholder}
            </label>
            <div className="relative">
                <select 
                    id={name}
                    {...register(name, validation)}
                     className={`w-full appearance-none px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border-2 transition-all duration-300
                        ${error ? 'border-red-500 focus:border-red-500 ring-red-500/20' : 'border-gray-200 dark:border-gray-600 focus:border-primary-500 ring-primary-500/20'}
                        text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                >
                    {children}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                     <ChevronDown className="w-5 h-5 text-gray-400" />
                </div>
            </div>
            {error && <p className="text-red-500 text-sm mt-1.5">{error.message}</p>}
        </div>
    ), []);

    const roles = [
        {
            icon: <Code className="w-8 h-8" />,
            title: "Full Stack Developers",
            description: "Skilled in both frontend and backend technologies with hands-on project experience.",
            skills: ["React", "Node.js", "JavaScript", "Python", "MongoDB", "MySQL"]
        },
        {
            icon: <Server className="w-8 h-8" />,
            title: "Backend Engineers",
            description: "Experts in server-side development, API design, and database management.",
            skills: ["Java", "Spring Boot", "REST APIs", "Microservices", "PostgreSQL", "Redis"]
        },
        {
            icon: <Layers className="w-8 h-8" />,
            title: "Frontend Specialists",
            description: "Creative developers focused on user experience and modern web technologies.",
            skills: ["React", "Vue.js", "TypeScript", "CSS3", "Responsive Design", "UI/UX"]
        },
        {
            icon: <CloudCog className="w-8 h-8" />,
            title: "Java Developers",
            description: "Full Stack Java Developers with Hands on project exposer.",
            skills: ["Java Core", "Spring", "Spring Boot", "MicroServices", "MySQL"]
        },
        {
            icon: <BarChart className="w-8 h-8" />,
            title: "Data Engineer",
            description: "Data Engineer with Hands on knowldge in latest ETL tools",
            skills: ["Python", "Snowflake", "Data Factory", "SQL", "Data Bricks", "dbt"]
        },
        {
            icon: <Bot className="w-8 h-8" />,
            title: "AI/ML Engineers",
            description: "Specialists in artificial intelligence and machine learning solutions.",
            skills: ["Python", "PyTorch", "Deep Learning", "NLP", "Computer Vision", "MLOps"]
        }
    ];

    const testimonials = [
        {
            name: "Sarah Johnson",
            role: "HR Director",
            company: "Microsoft",
            testimonial: "OSOP graduates consistently demonstrate exceptional technical skills and professionalism. They integrate seamlessly into our teams and contribute meaningfully from day one.",
            rating: 5
        },
        {
            name: "Rajesh Kumar",
            role: "Tech Lead",
            company: "Google",
            testimonial: "The quality of candidates from OSOP is outstanding. Their practical knowledge and problem-solving abilities make them valuable assets to any tech organization.",
            rating: 5
        },
        {
            name: "Emily Chen",
            role: "Engineering Manager",
            company: "Amazon",
            testimonial: "We've hired multiple developers from OSOP and they've all exceeded our expectations. Their training clearly prepares them for real-world challenges.",
            rating: 5
        }
    ];

    const hiringPartners = [
        { name: "Hiring Partners", logo: hiringPartnersGrid }
    ];

    return (
        <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 pt-20 overflow-hidden">
            {/* Enhanced Hero Section */}
            <section className="relative min-h-screen flex items-center">
                <div className="absolute inset-0 bg-gradient-professional opacity-95"></div>
                <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                
                <div className="container-max relative z-10 py-20">
                    <div className="text-center text-white">
                        <motion.div
                            className="inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-8"
                            {...fadeInUp}
                        >
                            <Briefcase className="w-5 h-5 mr-2 text-yellow-400" />
                            <span className="text-sm font-medium">Hire Tech Talent</span>
                        </motion.div>

                        <motion.h1 
                            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            Hire{' '}
                            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                                Job-Ready
                            </span>
                            {' '}Consultants
                        </motion.h1>

                        <motion.p 
                            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed text-gray-100"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Partner with OSOPCoding to access pool of highly skilled, industry-ready IT consultants. 
                            Our students carry in-depth knowledge from programming foudation to latest technologies & tools.
                        </motion.p>

                        <motion.div 
                            className="flex flex-col sm:flex-row justify-center gap-6 mb-16"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <a 
                                href="#hire-form" 
                                className="btn-hero bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                            >
                                <Users className="w-6 h-6 mr-3" />
                                Hire Now
                            </a>
                            <a 
                                href="#talent-pool" 
                                className="btn-hero bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                            >
                                <Trophy className="w-6 h-6 mr-3" />
                                Skills
                            </a>
                        </motion.div>

                        {/* Quick Stats */}
                        <motion.div 
                            className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto"
                            variants={staggerContainer}
                            initial="initial"
                            animate="animate"
                        >
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-yellow-400 mb-2">26</div>
                                <div className="text-gray-200">Years of Experience</div>
                            </motion.div>
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-green-400 mb-2">15K+</div>
                                <div className="text-gray-200">Students Trained</div>
                            </motion.div>
                            <motion.div className="text-center w-full md:w-1/3" variants={fadeInUp}>
                                <div className="text-4xl font-bold text-blue-400 mb-2">100%</div>
                                <div className="text-gray-200">Satisfied Learners</div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="container-max py-20 space-y-32">
                {/* Why Hire From OSOP */}
                <section>
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Why Hire From OSOP?</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Our comprehensive training program ensures that every graduate is industry-ready and equipped with the latest skills
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <StatCard 
                            icon={GraduationCap}
                            number="100%"
                            label="Industry-Ready"
                            description="Graduates trained with real-world projects"
                            color="bg-gradient-to-br from-blue-500 to-blue-600"
                            delay={0}
                        />
                        <StatCard 
                            icon={Award}
                            number="26+"
                            label="Years Of Training & Consulting"
                            description="Proven track record in tech education"
                            color="bg-gradient-to-br from-green-500 to-green-600"
                            delay={0.1}
                        />
                        <StatCard 
                            icon={Target}
                            number="100%"
                            label="Satisfied Learners"
                            description="Happy with our training programs"
                            color="bg-gradient-to-br from-purple-500 to-purple-600"
                            delay={0.2}
                        />
                        <StatCard 
                            icon={TrendingUp}
                            number="40%"
                            label="Faster Onboarding"
                            description="Reduced time-to-productivity"
                            color="bg-gradient-to-br from-orange-500 to-orange-600"
                            delay={0.3}
                        />
                    </div>
                </section>

                {/* Available Talent Pool */}
                <section id="talent-pool">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Skills</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Choose from different skills in technology domains, We will help you in getting skilled resource for your company
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {roles.map((role, index) => (
                            <RoleCard 
                                key={index}
                                {...role}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>
                </section>

                {/* Hiring Process */}
                <section className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 py-20 rounded-3xl">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Our Hiring Process</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            A streamlined process designed to connect you with the right talent quickly and efficiently
                        </p>
                    </motion.div>

                    <div className="max-w-4xl mx-auto">
                        <ProcessStep 
                            number="1"
                            title="Submit Requirements"
                            description="Share your hiring needs, tech stack requirements, and team preferences through our simple form."
                            icon={PenTool}
                            delay={0}
                        />
                        <ProcessStep 
                            number="2"
                            title="Candidate Matching"
                            description="Our team carefully matches candidates based on your specific requirements and company culture."
                            icon={Users}
                            delay={0.1}
                        />
                        <ProcessStep 
                            number="3"
                            title="Profile Review"
                            description="Review curated candidate profiles with detailed portfolios, project work, and skill assessments."
                            icon={User}
                            delay={0.2}
                        />
                        <ProcessStep 
                            number="4"
                            title="Interview & Selection"
                            description="Conduct interviews with pre-screened candidates and make your final selection."
                            icon={CheckCircle}
                            delay={0.3}
                        />
                        <ProcessStep 
                            number="5"
                            title="Onboarding Support"
                            description="Get ongoing support during the onboarding process to ensure smooth integration."
                            icon={Briefcase}
                            isLast={true}
                            delay={0.4}
                        />
                    </div>
                </section>

                
              {/* Hiring Form */}
                <section id="hire-form">
                    <motion.div 
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="heading-secondary mb-6">Start Hiring Today</h2>
                        <p className="text-lead max-w-3xl mx-auto">
                            Tell us about your requirements and we'll connect you with the perfect candidates
                        </p>
                    </motion.div>

                    <div className="max-w-4xl mx-auto">
                        <motion.div 
                            className="bg-white dark:bg-gray-800 p-12 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            {isSubmitted ? (
                                <motion.div 
                                    className="text-center py-12"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle className="w-10 h-10 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                        Request Submitted Successfully!
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                                        Thank you for your interest in hiring OSOPCoding Students. Our team will contact you within 24 hours to discuss your requirements.
                                    </p>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            <strong>What's Next?</strong><br />
                                            • Our hiring specialist will call you within 24 hours<br />
                                            • We'll discuss your specific requirements<br />
                                            • Receive curated candidate profiles within 48-72 hours
                                        </p>
                                    </div>
                                </motion.div>
                            ) : (
                                <>
                                    <div className="text-center mb-8">
                                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                                            Submit Your Hiring Request
                                        </h2>
                                        <p className="text-gray-600 dark:text-gray-400">Fill out the form below to start hiring top tech talent</p>
                                        
                                        {status === 'error' && (
                                            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                                <div className="flex items-center justify-center text-red-600 dark:text-red-400">
                                                    <AlertTriangle className="w-5 h-5 mr-2" />
                                                    <span className="font-medium">Submission Failed</span>
                                                </div>
                                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                    {errorMessage || 'Please check your internet connection and try again, or call us directly.'}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="mb-8">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Form Progress</span>
                                            <span className="text-sm font-medium text-primary-600 dark:text-primary-400">{progress}% Complete</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                            <motion.div 
                                                className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full"
                                                style={{ width: `${progress}%` }}
                                                initial={{ width: 0 }}
                                                animate={{ width: `${progress}%` }}
                                                transition={{ duration: 0.5 }}
                                            />
                                        </div>
                                    </div>

                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                                        <div className="grid md:grid-cols-2 gap-8">
                                            <Input 
                                                key="companyName"
                                                name="companyName"
                                                placeholder="Company Name *"
                                                validation={{ required: "Company name is required" }}
                                                error={errors.companyName}
                                                register={register}
                                            />
                                            <Input 
                                                key="contactPerson"
                                                name="contactPerson"
                                                placeholder="Contact Person *"
                                                validation={{ required: "Contact person is required" }}
                                                error={errors.contactPerson}
                                                register={register}
                                            />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-8">
                                            <Input 
                                                key="email"
                                                name="email"
                                                type="email"
                                                placeholder="Email Address *"
                                                validation={{ 
                                                    required: "Email is required",
                                                    pattern: {
                                                        value: /^\S+@\S+$/i,
                                                        message: "Invalid email address"
                                                    }
                                                }}
                                                error={errors.email}
                                                register={register}
                                            />
                                            <Input 
                                                key="phone"
                                                name="phone"
                                                type="tel"
                                                placeholder="Phone Number *"
                                                validation={{ required: "Phone number is required" }}
                                                error={errors.phone}
                                                register={register}
                                            />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-8">
                                            <Select 
                                                key="roleType"
                                                name="roleType"
                                                placeholder="Role Type *"
                                                validation={{ required: "Please select a role type" }}
                                                error={errors.roleType}
                                                register={register}
                                            >
                                                <option value="">Select Role Type</option>
                                                <option value="fullstack">Full Stack Developer</option>
                                                <option value="frontend">Frontend Developer</option>
                                                <option value="backend">Backend Developer</option>
                                                <option value="mobile">Java Developer</option>
                                                <option value="devops">DevOps Engineer</option>
                                                <option value="data">Data Engineer</option>
                                                <option value="ai">AI/ML Engineer</option>
                                            </Select>
                                            <Select 
                                                key="experience"
                                                name="experience"
                                                placeholder="Experience Level *"
                                                validation={{ required: "Please select experience level" }}
                                                error={errors.experience}
                                                register={register}
                                            >
                                                <option value="">Select Experience</option>
                                                <option value="fresher">Fresher (0-1 years)</option>
                                                <option value="junior">Junior (1-3 years)</option>
                                                <option value="mid">Mid-level (3-5 years)</option>
                                                <option value="senior">Senior (5+ years)</option>
                                            </Select>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-8">
                                            <Input 
                                                key="positions"
                                                name="positions"
                                                type="number"
                                                placeholder="Number of Positions *"
                                                validation={{ 
                                                    required: "Number of positions is required",
                                                    min: { value: 1, message: "At least 1 position required" }
                                                }}
                                                error={errors.positions}
                                                register={register}
                                            />
                                            <Select 
                                                key="urgency"
                                                name="urgency"
                                                placeholder="Hiring Urgency *"
                                                validation={{ required: "Please select urgency" }}
                                                error={errors.urgency}
                                                register={register}
                                            >
                                                <option value="">Select Urgency</option>
                                                <option value="urgent">Urgent (Within 1 week)</option>
                                                <option value="high">High (Within 2 weeks)</option>
                                                <option value="medium">Medium (Within 1 month)</option>
                                                <option value="low">Low (2+ months)</option>
                                            </Select>
                                        </div>

                                        <div>
                                            <label className="font-medium text-gray-700 dark:text-gray-300 mb-2 block text-sm">
                                                Additional Requirements (Optional)
                                            </label>
                                            <textarea 
                                                {...register("requirements")}
                                                rows="4"
                                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none transition-all duration-300"
                                                placeholder="Please describe specific skills, technologies, or requirements for the role..."
                                            />
                                        </div>
                                        
                                        <div>
                                            {status === 'idle' && (
                                                <motion.button 
                                                    type="submit" 
                                                    disabled={progress < 100} 
                                                    className="w-full btn btn-primary-gradient btn-lg rounded-2xl px-10 py-4 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                                    whileHover={{ scale: progress >= 100 ? 1.02 : 1 }}
                                                    whileTap={{ scale: progress >= 100 ? 0.98 : 1 }}
                                                >
                                                    <Sparkles className="w-6 h-6 mr-3" />
                                                    Submit Hiring Request
                                                    <ArrowRight className="w-6 h-6 ml-3" />
                                                </motion.button>
                                            )}
                                            {status === 'loading' && (
                                                <button 
                                                    type="button" 
                                                    disabled 
                                                    className="w-full btn btn-primary-gradient btn-lg rounded-2xl px-10 py-4 opacity-70 inline-flex items-center justify-center"
                                                >
                                                    <RefreshCw className="w-6 h-6 mr-3 animate-spin" /> 
                                                    Submitting Your Request...
                                                </button>
                                            )}
                                            {status === 'error' && (
                                                <div className="space-y-4">
                                                    <div className="text-center p-6 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                                                        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                                                        <p className="font-bold text-red-700 dark:text-red-300">Submission Failed</p>
                                                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                                            {errorMessage || 'Please check your internet connection and try again, or contact us directly.'}
                                                        </p>
                                                    </div>
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            setStatus('idle');
                                                            setErrorMessage('');
                                                        }}
                                                        className="w-full btn btn-outline btn-lg rounded-2xl px-10 py-4"
                                                    >
                                                        Try Again
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </form>
                                </>
                            )}
                        </motion.div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="text-center bg-gradient-professional p-16 rounded-3xl shadow-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-dots-dark opacity-20"></div>
                    <div className="relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Ready to Hire new Talent?
                            </h2>
                            <p className="text-xl text-gray-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                                Many TOP MNCs directly hired top talent from OSOPCoding, We provide on-demand resourcing as per your Project Requirements. 
                                Let us know your requirements?.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-6">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <a 
                                        href="#hire-form" 
                                        className="btn-hero bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <Users className="w-6 h-6 mr-3" />
                                        Start Hiring Now
                                        <ArrowRight className="w-6 h-6 ml-3" />
                                    </a>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <a 
                                        href="tel:+917649098000" 
                                        className="btn-hero bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-glow-lg inline-flex items-center justify-center"
                                    >
                                        <Phone className="w-6 h-6 mr-3" />
                                        Call Us: +91 76490 98000
                                    </a>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default Hire;
 